<template>
  <div class="m-0 h-full">
    <PaymentBreakdownHeader
      :show="showPaymentBreakdown"
      @back="back"
      @toggle="showPaymentBreakdown = !showPaymentBreakdown"
    />
    <PaymentBreakdownSchedule :show="showPaymentBreakdown" :titles="titles" :payments="convertedPayments" />
    <SectionDivider />
    <template v-if="showCancellationPolicy">
      <CancellationPolicy :deposit="paymentPlanPreview.depositDue" :total="total" :balance="total" />
      <SectionDivider />
    </template>
  </div>
</template>

<script setup lang="ts">
import SectionDivider from '@/components/common/SectionDivider.vue';
import CancellationPolicy from '@/components/widget/payments-breakdown/sections/CancellationPolicy.vue';
import PaymentBreakdownHeader from '@/components/widget/payments-breakdown/sections/PaymentBreakdownHeader.vue';
import PaymentBreakdownSchedule from '@/components/widget/payments-breakdown/sections/PaymentBreakdownSchedule.vue';
import type { ITrimmedPaymentPlan } from '@/models/paymentPlans';
import type { IPayment, PaymentSummary } from '@/models/payments';
import { useWidgetStore } from '@/stores/widget';
import { displayCurrency, formatDateMedium } from '@/utils/format.utils';
import { hasFullCancellationPolicy } from '@/utils/order-cancellation-terms';
import { pluralOrSingularText } from '@/utils/text.utils';
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import type { ScheduleBreakdownPayment } from './payments-breakdown.types';

const router = useRouter();
const convertedPayments = ref([] as ScheduleBreakdownPayment[]);
const instalmentAmount = ref('');
const quantity = ref(0);
const total = ref(0);
const deposit = ref(0);
const showPaymentBreakdown = ref(true);
const widgetStore = useWidgetStore();
const paymentPlanPreview = widgetStore.paymentPlanPreview;
const titles = ref<PaymentSummary[]>([]);
const incompletePaymentPlan = widgetStore.layby;

const showCancellationPolicy = computed(() => !hasFullCancellationPolicy(widgetStore.order?.cancellationTerms));

if (paymentPlanPreview && incompletePaymentPlan) {
  total.value = paymentPlanPreview.depositDue + paymentPlanPreview.amountDue;
  const selectedPlan = widgetStore?.paymentPlanPreview?.paymentPlans?.filter(
    (pp) => pp.quantity === incompletePaymentPlan.duration,
  )[0] as ITrimmedPaymentPlan;
  if (selectedPlan) {
    deposit.value = selectedPlan.deposit;
  } else {
    deposit.value = paymentPlanPreview.depositDue;
  }

  quantity.value = incompletePaymentPlan.duration;

  const payments = paymentPlanPreview.paymentPlans?.filter((pp) => pp.quantity === quantity.value)[0]
    ?.payments as unknown as IPayment[];

  if (payments) {
    convertedPayments.value = payments
      .filter((p: IPayment, index: number) => index !== 0)
      .map((p: any) => {
        return {
          id: p._id!,
          amount: displayCurrency(p.amount),
          due: formatDateMedium(p.due),
        };
      }) as ScheduleBreakdownPayment[];

    instalmentAmount.value = displayCurrency(payments[1].amount || 0);
  }
  buildTitles();
} else {
  router.push('./');
}

function buildTitles() {
  titles.value = [
    {
      label: 'Total Order Value',
      value: displayCurrency(total.value),
      grey: false,
    },
    { label: 'Monthly Amount', value: instalmentAmount.value, grey: false },
    { label: 'Deposit', value: displayCurrency(deposit.value), grey: false },
    { label: 'Duration', value: `${quantity.value} ${pluralOrSingularText(quantity.value, 'month')}`, grey: false },
  ];
}

const back = () => {
  router.back();
};
</script>
